import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_BASE_URL = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";

export default function AdminFrontendView() {
  const [activeTab, setActiveTab] = useState("Activity");
  const [loading, setLoading] = useState(false);

  // Common UI State
  const tabs = ["Activity", "Visa", "Cab", "Tour"];

  // Activity State
  const [actCountries, setActCountries] = useState([]);
  const [actCities, setActCities] = useState([]);
  const [actSelectedCountry, setActSelectedCountry] = useState("");
  const [actSelectedCity, setActSelectedCity] = useState("");
  const [activities, setActivities] = useState([]);

  // Visa State
  const [visas, setVisas] = useState([]);

  // Tour State
  const [tourCountries, setTourCountries] = useState([]);
  const [tourSelectedCountry, setTourSelectedCountry] = useState("");
  const [tours, setTours] = useState([]);

  // Cab State
  const [cabCountries, setCabCountries] = useState([]);
  const [cabCities, setCabCities] = useState([]);
  const [cabServices, setCabServices] = useState([]);
  const [cabSelectedCountry, setCabSelectedCountry] = useState("");
  const [cabSelectedCity, setCabSelectedCity] = useState("");
  const [cabSelectedService, setCabSelectedService] = useState("");
  const [cabs, setCabs] = useState([]);

  // Fetch Config
  const axiosConfig = { withCredentials: true };

  useEffect(() => {
    if (activeTab === "Activity" && actCountries.length === 0) fetchActCountries();
    if (activeTab === "Visa" && visas.length === 0) fetchVisas();
    if (activeTab === "Tour" && tourCountries.length === 0) fetchTourCountries();
    if (activeTab === "Cab" && cabCountries.length === 0) fetchCabData();
  }, [activeTab]);

  // --- ACTIVITY FETCHERS ---
  const fetchActCountries = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/activity/admin/countries`, axiosConfig);
      setActCountries(res.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchActCities = async (countryId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/activity/admin/cities/${countryId}`, axiosConfig);
      setActCities(res.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchActivities = async (cityId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/activity/admin/activities/${cityId}`, axiosConfig);
      setActivities(res.data?.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // --- VISA FETCHERS ---
  const fetchVisas = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/visa-info`, axiosConfig);
      setVisas(res.data?.visas || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // --- TOUR FETCHERS ---
  const fetchTourCountries = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/tour/admin/countries`, axiosConfig);
      setTourCountries(res.data?.data || []);
      const pRes = await axios.get(`${API_BASE_URL}/api/tour/admin/packages`, axiosConfig);
      setTours(pRes.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  // --- CAB FETCHERS ---
  const fetchCabData = async () => {
    try {
      const cRes = await axios.get(`${API_BASE_URL}/api/cab/admin/countries`, axiosConfig);
      setCabCountries(cRes.data?.data || []);
      
      const cityRes = await axios.get(`${API_BASE_URL}/api/cab/admin/cities`, axiosConfig);
      setCabCities(cityRes.data?.data || []);
      
      const sRes = await axios.get(`${API_BASE_URL}/api/cab/admin/services`, axiosConfig);
      setCabServices(sRes.data?.data || []);
      
      const pRes = await axios.get(`${API_BASE_URL}/api/cab/admin/pricing`, axiosConfig);
      setCabs(pRes.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleFeature = async (id, currentStatus, type) => {
    try {
      await axios.put(`${API_BASE_URL}/api/frontend-features`, {
        itemType: type,
        updates: [{ id, isFeatured: !currentStatus }]
      }, axiosConfig);
      
      toast.success("Updated Successfully");
      
      // Update local state to reflect change instantly
      if (type === "Activity") {
        setActivities(activities.map(a => a._id === id ? { ...a, isFeatured: !currentStatus } : a));
      } else if (type === "Visa") {
        setVisas(visas.map(v => v._id === id ? { ...v, isFeatured: !currentStatus } : v));
      } else if (type === "Tour") {
        setTours(tours.map(t => t._id === id ? { ...t, isFeatured: !currentStatus } : t));
      } else if (type === "Cab") {
        setCabs(cabs.map(c => c._id === id ? { ...c, isFeatured: !currentStatus } : c));
      }
    } catch (e) {
      console.error(e);
      toast.error("Error updating item");
    }
  };

  const renderActivityTab = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Country</label>
          <select 
            className="w-full p-2 border rounded" 
            value={actSelectedCountry}
            onChange={(e) => {
              setActSelectedCountry(e.target.value);
              setActSelectedCity("");
              setActivities([]);
              if (e.target.value) fetchActCities(e.target.value);
            }}
          >
            <option value="">Select Country</option>
            {actCountries.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">City</label>
          <select 
            className="w-full p-2 border rounded"
            value={actSelectedCity}
            onChange={(e) => {
              setActSelectedCity(e.target.value);
              if (e.target.value) fetchActivities(e.target.value);
            }}
            disabled={!actSelectedCountry}
          >
            <option value="">Select City</option>
            {actCities.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
      </div>
      
      {loading ? <p>Loading activities...</p> : (
        <div className="mt-4 border rounded overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Show</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity Name</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activities.length === 0 && <tr><td colSpan="2" className="p-4 text-center text-gray-500">No activities found</td></tr>}
              {activities.map(a => (
                <tr key={a._id} className={a.isFeatured ? "bg-blue-50" : ""}>
                  <td className="px-6 py-4">
                    <input 
                      type="checkbox" 
                      className="w-5 h-5 text-blue-600 rounded cursor-pointer"
                      checked={!!a.isFeatured}
                      onChange={() => toggleFeature(a._id, !!a.isFeatured, "Activity")}
                    />
                  </td>
                  <td className="px-6 py-4 font-medium">{a.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderVisaTab = () => (
    <div className="space-y-4">
      {loading ? <p>Loading visas...</p> : (
        <div className="border rounded overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Show</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {visas.length === 0 && <tr><td colSpan="3" className="p-4 text-center text-gray-500">No visas found</td></tr>}
              {visas.map(v => (
                <tr key={v._id} className={v.isFeatured ? "bg-blue-50" : ""}>
                  <td className="px-6 py-4">
                    <input 
                      type="checkbox" 
                      className="w-5 h-5 text-blue-600 rounded cursor-pointer"
                      checked={!!v.isFeatured}
                      onChange={() => toggleFeature(v._id, !!v.isFeatured, "Visa")}
                    />
                  </td>
                  <td className="px-6 py-4 font-medium">{v.country}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{v.description ? v.description.substring(0, 50) : ""}...</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderTourTab = () => {
    const filteredTours = tourSelectedCountry 
      ? tours.filter(t => (t.tourCountryId?._id || t.tourCountryId) === tourSelectedCountry)
      : tours;

    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Country</label>
          <select 
            className="w-full md:w-1/2 p-2 border rounded" 
            value={tourSelectedCountry}
            onChange={(e) => setTourSelectedCountry(e.target.value)}
          >
            <option value="">All Countries</option>
            {tourCountries.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
        
        <div className="border rounded overflow-hidden mt-4">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Show</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTours.length === 0 && <tr><td colSpan="3" className="p-4 text-center text-gray-500">No tours found</td></tr>}
              {filteredTours.map(t => (
                <tr key={t._id} className={t.isFeatured ? "bg-blue-50" : ""}>
                  <td className="px-6 py-4">
                    <input 
                      type="checkbox" 
                      className="w-5 h-5 text-blue-600 rounded cursor-pointer"
                      checked={!!t.isFeatured}
                      onChange={() => toggleFeature(t._id, !!t.isFeatured, "Tour")}
                    />
                  </td>
                  <td className="px-6 py-4 font-medium">{t.title}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{t.cityName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCabTab = () => {
    // Filter cabs based on selections
    const filteredCabs = cabs.filter(c => {
      if (cabSelectedCountry && (c.countryId?._id || c.countryId) !== cabSelectedCountry) return false;
      if (cabSelectedCity && (c.cityId?._id || c.cityId) !== cabSelectedCity) return false;
      if (cabSelectedService && (c.serviceTypeId?._id || c.serviceTypeId) !== cabSelectedService) return false;
      return true;
    });

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Country</label>
            <select 
              className="w-full p-2 border rounded" 
              value={cabSelectedCountry}
              onChange={(e) => setCabSelectedCountry(e.target.value)}
            >
              <option value="">All Countries</option>
              {cabCountries.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">City</label>
            <select 
              className="w-full p-2 border rounded" 
              value={cabSelectedCity}
              onChange={(e) => setCabSelectedCity(e.target.value)}
            >
              <option value="">All Cities</option>
              {cabCities.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Service Type</label>
            <select 
              className="w-full p-2 border rounded" 
              value={cabSelectedService}
              onChange={(e) => setCabSelectedService(e.target.value)}
            >
              <option value="">All Services</option>
              {cabServices.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div className="border rounded overflow-hidden mt-4">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Show</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cab Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base Fare</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCabs.length === 0 && <tr><td colSpan="3" className="p-4 text-center text-gray-500">No cabs found</td></tr>}
              {filteredCabs.map(c => (
                <tr key={c._id} className={c.isFeatured ? "bg-blue-50" : ""}>
                  <td className="px-6 py-4">
                    <input 
                      type="checkbox" 
                      className="w-5 h-5 text-blue-600 rounded cursor-pointer"
                      checked={!!c.isFeatured}
                      onChange={() => toggleFeature(c._id, !!c.isFeatured, "Cab")}
                    />
                  </td>
                  <td className="px-6 py-4 font-medium">{c.cabTypeId?.name || "Unknown"}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">₹{c.baseFare}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Frontend View Management</h2>
      
      <div className="flex border-b border-gray-200 mb-6 space-x-8">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${
              activeTab === tab
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-4">
        {activeTab === "Activity" && renderActivityTab()}
        {activeTab === "Visa" && renderVisaTab()}
        {activeTab === "Tour" && renderTourTab()}
        {activeTab === "Cab" && renderCabTab()}
      </div>
    </div>
  );
}
